/**
 * Leaderboard Page Component
 *
 * Thin rendering layer — no sorting, filtering, or rank calculation.
 * All data arrives pre-computed from the consolidated leaderboard API.
 *
 * - Single API call fetches all page data (leaderboards, charts, stats)
 * - Pre-ranked Students and Coaches sections
 * - RSVP Leaderboard section
 * - Event participation bar chart
 * - Statistics charts (attendance, participation, event type distribution)
 * - Season dropdown is the only filter
 *
 * Requirements: 1.2, 1.3, 1.4, 2.1, 2.2, 2.3, 2.4, 2.6, 3.2, 3.5, 3.6,
 *   4.1, 4.2, 5.1, 5.2, 5.3, 5.4, 5.5, 6.1, 6.2, 6.3, 7.1, 7.2, 7.3, 7.4,
 *   9.1, 9.5, 9.6, 9.7, 9.8
 */

import { useCallback, useEffect, useState } from 'react';
import {
  restClient,
  type LeaderboardEntry,
  type RSVPLeaderboardEntry,
  type LeaderboardPageResponse,
} from '../api/rest-client';
import { Card } from '../components/common/Card';
import { Button } from '../components/common/Button';
import { LoadingSpinner } from '../components/common/LoadingSpinner';
import { formatRiderName, getRiderBadgeText, getRiderBadgeColor } from '../utils/riderDisplay';
import {
  ParticipationChart,
  PerformanceChart,
  type ParticipationData,
  type PerformanceData,
} from '../components/features/statistics';

/**
 * Get medal emoji for top 3 positions
 * Requirements: 5.1, 9.6
 */
export function getMedalEmoji(rank: number): string {
  switch (rank) {
    case 1:
      return '🥇';
    case 2:
      return '🥈';
    case 3:
      return '🥉';
    default:
      return '';
  }
}

/**
 * Get medal background color for top 3
 * Requirements: 5.1
 */
export function getMedalBgColor(rank: number): string {
  switch (rank) {
    case 1:
      return 'bg-yellow-400 text-yellow-900';
    case 2:
      return 'bg-gray-300 text-gray-800';
    case 3:
      return 'bg-orange-400 text-orange-900';
    default:
      return 'bg-primary-200 text-primary-800';
  }
}

/**
 * Leaderboard section component for rendering a ranked list.
 * Used for Students, Coaches, and RSVP sections to avoid duplication.
 * Collapses to top 10 entries by default when list exceeds threshold.
 */
function LeaderboardSection({
  title,
  entries,
  emptyMessage,
  renderStats,
  season,
  hideBadge = false,
  stackStats = false,
}: {
  title: string;
  entries: (LeaderboardEntry | RSVPLeaderboardEntry)[];
  emptyMessage: string;
  renderStats: (entry: LeaderboardEntry | RSVPLeaderboardEntry) => React.ReactNode;
  season: string;
  /** Hide the rider type badge pill (e.g. "Coach") */
  hideBadge?: boolean;
  /** Stack stats below the name instead of right-aligned */
  stackStats?: boolean;
}) {
  const COLLAPSE_THRESHOLD = 10;
  const [expanded, setExpanded] = useState(false);

  // Reset collapse state when season changes
  useEffect(() => {
    setExpanded(false);
  }, [season]);

  const displayedEntries =
    entries.length > COLLAPSE_THRESHOLD && !expanded
      ? entries.slice(0, COLLAPSE_THRESHOLD)
      : entries;

  const sectionId = title.toLowerCase().replace(/\s+/g, '-');

  return (
    <div className="mb-8">
      <h2 className="text-xl font-bold text-primary-900 mb-4" data-testid={`section-header-${sectionId}`}>
        {title}
      </h2>
      {entries.length === 0 ? (
        <div className="text-center py-8 text-primary-600" data-testid={`empty-${sectionId}`}>
          <p>{emptyMessage}</p>
        </div>
      ) : (
        <>
          <div className="space-y-2" data-testid={`leaderboard-list-${sectionId}`}>
            {displayedEntries.map((entry) => (
              <div
                key={`${entry.rider_id}-${entry.rank}`}
                className={`flex items-center ${stackStats ? '' : 'justify-between'} p-2 sm:p-4 rounded-lg transition-colors ${
                  entry.rank <= 3 ? 'bg-primary-100' : 'bg-primary-50'
                } hover:bg-primary-200`}
                data-testid="leaderboard-entry"
              >
                <div className={`flex items-center gap-2 sm:gap-4 ${stackStats ? 'min-w-0 flex-1' : 'min-w-0'}`}>
                  {/* Rank Badge */}
                  <div
                    className={`w-8 h-8 sm:w-12 sm:h-12 shrink-0 rounded-full flex items-center justify-center font-bold text-sm sm:text-lg ${getMedalBgColor(entry.rank)}`}
                    data-testid="rank-badge"
                  >
                    {entry.rank <= 3 ? (
                      <span className="text-lg sm:text-2xl">{getMedalEmoji(entry.rank)}</span>
                    ) : (
                      entry.rank
                    )}
                  </div>

                  {/* Name, Type, and optionally stacked Stats */}
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-1 sm:gap-2">
                      <p className={`font-semibold text-primary-900 text-base sm:text-lg ${stackStats ? '' : 'truncate'}`}>
                        {formatRiderName(entry)}
                      </p>
                      {!hideBadge && getRiderBadgeText(entry) && (
                        <span
                          className={`shrink-0 inline-block px-1.5 sm:px-2 py-0.5 text-xs font-semibold text-white ${getRiderBadgeColor(entry)} rounded-full`}
                        >
                          {getRiderBadgeText(entry)}
                        </span>
                      )}
                    </div>
                    <p className="text-xs sm:text-sm text-primary-600">{entry.type}</p>
                    {stackStats && (
                      <div className="mt-1">{renderStats(entry)}</div>
                    )}
                  </div>
                </div>

                {/* Stats — right-aligned when not stacked */}
                {!stackStats && (
                  <div className="text-right shrink-0 ml-2">{renderStats(entry)}</div>
                )}
              </div>
            ))}
          </div>
          {entries.length > COLLAPSE_THRESHOLD && (
            <button
              onClick={() => setExpanded(!expanded)}
              aria-expanded={expanded}
              className="mt-3 w-full text-center py-2 text-sm font-medium text-primary-700 hover:text-primary-900 hover:bg-primary-100 rounded-lg transition-colors"
              data-testid={`toggle-${sectionId}`}
            >
              {expanded ? 'Show top 10 only' : `Show all ${entries.length} entries`}
            </button>
          )}
        </>
      )}
    </div>
  );
}

export default function Leaderboard() {
  // Season state — the only remaining filter
  const [season, setSeason] = useState<string>(new Date().getFullYear().toString());
  const [availableSeasons, setAvailableSeasons] = useState<string[]>([new Date().getFullYear().toString()]);

  // Single loading/error state for the entire page (one API call)
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Consolidated API response data — stored directly, no processing
  const [studentsLeaderboard, setStudentsLeaderboard] = useState<LeaderboardEntry[]>([]);
  const [coachesLeaderboard, setCoachesLeaderboard] = useState<LeaderboardEntry[]>([]);
  const [rsvpLeaderboard, setRsvpLeaderboard] = useState<RSVPLeaderboardEntry[]>([]);
  const [weeklyParticipation, setWeeklyParticipation] = useState<LeaderboardPageResponse['weekly_participation']>([]);
  const [eventTypeDistribution, setEventTypeDistribution] = useState<LeaderboardPageResponse['event_type_distribution']>([]);

  /**
   * Fetch season configuration on mount
   * Requirements: 1.1, 6.4
   */
  useEffect(() => {
    fetchSeasonConfig();
  }, []);

  /**
   * Fetch all leaderboard page data when season changes — single API call
   * Requirements: 1.2, 1.3, 6.1, 6.2, 6.3
   */
  useEffect(() => {
    fetchLeaderboard();
  }, [season]);

  const fetchSeasonConfig = useCallback(async () => {
    try {
      const config = await restClient.getSeasonConfig();
      setAvailableSeasons(config.availableSeasons);
      if (config.availableSeasons.includes(config.currentSeason)) {
        setSeason(config.currentSeason);
      }
    } catch {
      // Keep default (current year) if API call fails
    }
  }, []);

  /**
   * Fetch consolidated leaderboard data — single API call replaces both
   * getLeaderboard and getStatistics calls.
   * Requirements: 1.2, 1.3, 6.1, 6.2, 6.3
   */
  const fetchLeaderboard = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const data = await restClient.getLeaderboard({ season, limit: 100 });

      // Destructure response — default missing arrays to [] for backward compatibility
      setStudentsLeaderboard(data.students_leaderboard ?? []);
      setCoachesLeaderboard(data.coaches_leaderboard ?? []);
      setRsvpLeaderboard(data.rsvp_leaderboard ?? []);
      setWeeklyParticipation(data.weekly_participation ?? []);
      setEventTypeDistribution(data.event_type_distribution ?? []);
    } catch {
      setError('Failed to load leaderboard data. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [season]);

  // Transform data for chart components (match their expected interfaces)
  const participationData: ParticipationData[] = weeklyParticipation.map((w) => ({
    date: w.date,
    events: w.events,
    hours: w.hours,
  }));

  const performanceData: PerformanceData[] = eventTypeDistribution.map((e) => ({
    category: e.category,
    value: e.value,
  }));

  return (
    <div className="min-h-screen bg-primary-50">
      {/* Header — displays season year */}
      <div className="bg-white shadow">
        <div className="container mx-auto px-4 py-4">
          <h1 className="text-2xl sm:text-3xl font-bold text-primary-900">Leaderboard</h1>
          <p className="text-primary-600 mt-1">
            Top participants for the {season} season
          </p>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-4 pt-3 pb-8">
        {/* Season Filter — only remaining filter */}
        <Card className="mb-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1 max-w-xs">
              <label
                htmlFor="season-select"
                className="block text-sm font-medium text-primary-700 mb-2"
              >
                Season
              </label>
              <select
                id="season-select"
                value={season}
                onChange={(e) => setSeason(e.target.value)}
                className="w-full px-4 py-2 border border-primary-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white"
                data-testid="season-select"
              >
                {availableSeasons.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </Card>

        {/* Loading State — single spinner for entire page */}
        {loading && (
          <div className="flex justify-center items-center py-12" data-testid="loading-spinner">
            <LoadingSpinner size="lg" />
          </div>
        )}

        {/* Error State with Try Again button */}
        {error && !loading && (
          <Card className="mb-6">
            <div className="text-center py-12">
              <div className="text-danger-500 mb-4">
                <svg
                  className="w-16 h-16 mx-auto"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </div>
              <p className="text-primary-600 mb-4">{error}</p>
              <Button variant="primary" onClick={fetchLeaderboard} data-testid="try-again-button">
                Try Again
              </Button>
            </div>
          </Card>
        )}

        {/* Page content — only shown when not loading and no error */}
        {!loading && !error && (
          <>
            {/* Students Leaderboard Section */}
            <Card className="mb-6">
              <LeaderboardSection
                title="Students"
                entries={studentsLeaderboard}
                emptyMessage="No student data available for this season"
                season={season}
                stackStats
                renderStats={(entry) => {
                  const e = entry as LeaderboardEntry;
                  return (
                    <p className="text-sm sm:text-base text-primary-700" data-testid="entry-events">
                      <span className="font-semibold">{e.events}</span> events · <span className="font-semibold">{e.hours.toFixed(1)}</span> hrs
                    </p>
                  );
                }}
              />
            </Card>

            {/* Coaches Leaderboard Section */}
            <Card className="mb-6">
              <LeaderboardSection
                title="Coaches"
                entries={coachesLeaderboard}
                emptyMessage="No coach data available for this season"
                season={season}
                hideBadge
                stackStats
                renderStats={(entry) => {
                  const e = entry as LeaderboardEntry;
                  return (
                    <p className="text-sm sm:text-base text-primary-700" data-testid="entry-events">
                      <span className="font-semibold">{e.events}</span> events · <span className="font-semibold">{e.hours.toFixed(1)}</span> hrs
                    </p>
                  );
                }}
              />
            </Card>

            {/* RSVP Leaderboard Section */}
            <Card className="mb-6">
              <LeaderboardSection
                title="RSVP Leaderboard"
                entries={rsvpLeaderboard}
                emptyMessage="No RSVP data available for this season"
                season={season}
                hideBadge
                stackStats
                renderStats={(entry) => {
                  const e = entry as RSVPLeaderboardEntry;
                  return (
                    <p className="text-sm sm:text-base text-primary-700" data-testid="entry-rsvp-count">
                      <span className="font-semibold">{e.rsvp_count}</span> RSVPs
                    </p>
                  );
                }}
              />
            </Card>

            {/* Statistics Charts — responsive grid */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {participationData.length > 0 && (
                <Card>
                  <ParticipationChart data={participationData} title="Weekly Events" />
                </Card>
              )}

              {performanceData.length > 0 && (
                <Card>
                  <PerformanceChart data={performanceData} title="Events by Type" />
                </Card>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
